// src/components/StatusPage/StatusPage.js
//
// Wasabi Red Team Status Page. Company-wide pentest portfolio table, mirrors
// (and replaces) the manually-maintained Confluence "Completed Penetration
// Tests" table. Open to both Pentester and Wasabian roles.
//
// Each row shows: PT name + type, date completed, grade, severity counts,
// optional external report link (legacy PTs imported from M365 / Confluence),
// and operator notes. The "Copy as Markdown" button emits a Confluence-paste-
// ready markdown table so the operator can keep Confluence in sync with one
// click (until the auto-import work lands - see project memory).

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ClipboardCheck, Copy, ExternalLink, Filter, Pencil, RefreshCw, X } from 'lucide-react';
import { pentestsAPI, statusPageAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { HeroStrip } from '../common/Shinobi';
import { parseServerDate } from '../../utils/time';
import { StatusChip, StatusSelect, STATUS_META } from '../Pentests/PentestStatus';

const TYPE_LABEL = {
  external: 'External',
  internal: 'Internal',
  third_party_external: '3rd-party External',
  third_party_internal: '3rd-party Internal',
};

const STATUS_LABEL = Object.fromEntries(
  Object.entries(STATUS_META).map(([k, v]) => [k, v.label]),
);

const GRADE_PILL = {
  A: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  B: 'bg-lime-100 text-lime-700 border-lime-300',
  C: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  D: 'bg-orange-100 text-orange-700 border-orange-300',
  F: 'bg-red-100 text-red-700 border-red-300',
};

function fmtDate(iso) {
  if (!iso) return '-';
  const d = parseServerDate(iso);
  if (!d || isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function StatusPage() {
  const { isPentester } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInProgress, setIncludeInProgress] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await statusPageAPI.get({ includeInProgress });
      setData(r);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onRowSaved = (updatedPentest) => {
    // PATCH returns the full PentestResponse. Update the row in place so we
    // don't have to re-fetch the whole portfolio just for one row.
    setData((prev) => {
      if (!prev) return prev;
      const rows = prev.rows.map((r) => {
        if (r.id !== updatedPentest.id) return r;
        return {
          ...r,
          name: updatedPentest.name,
          status: updatedPentest.status,
          ended_at: updatedPentest.ended_at,
          external_report_url: updatedPentest.external_report_url || null,
          notes: updatedPentest.notes || null,
          severity_counts: updatedPentest.severity_counts || r.severity_counts,
          finding_count: updatedPentest.finding_count ?? r.finding_count,
          grade: updatedPentest.grade ?? r.grade,
          overall_score: updatedPentest.overall_score ?? r.overall_score,
        };
      });
      return { ...prev, rows };
    });
    setEditingId(null);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInProgress]);

  const rawRows = data?.rows || [];

  // Group rows so retests appear immediately under their parent (indented).
  // Server returns a flat list ordered by ended_at desc; we walk the parents
  // in that order and slot each parent's children under it. Orphan retests
  // (parent_pentest_id pointing nowhere in the response) fall through to the
  // end as a flat list so they remain visible.
  const rows = useMemo(() => {
    const parents = rawRows.filter((r) => r.parent_pentest_id == null);
    const childrenByParent = new Map();
    for (const r of rawRows) {
      if (r.parent_pentest_id != null) {
        const list = childrenByParent.get(r.parent_pentest_id) || [];
        list.push(r);
        childrenByParent.set(r.parent_pentest_id, list);
      }
    }
    const out = [];
    const placedIds = new Set();
    for (const p of parents) {
      out.push(p);
      placedIds.add(p.id);
      const kids = (childrenByParent.get(p.id) || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
      for (const k of kids) {
        out.push(k);
        placedIds.add(k.id);
      }
    }
    // Orphan retests (parent not in the response, e.g. archived + filtered out)
    for (const r of rawRows) {
      if (!placedIds.has(r.id)) out.push(r);
    }
    return out;
  }, [rawRows]);

  const aggregateSeverity = useMemo(() => {
    // Aggregate over PARENTS ONLY so a PT with retests doesn't get double-counted.
    // The parent row's severity_counts already reflects the most-recent-retest rollup.
    const totals = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    for (const r of rows) {
      if (r.parent_pentest_id != null) continue;
      for (const k of Object.keys(totals)) {
        totals[k] += (r.severity_counts?.[k] || 0);
      }
    }
    return totals;
  }, [rows]);

  const onCopyMarkdown = async () => {
    const md = renderMarkdown(rows);
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setError(`Could not copy: ${e.message}`);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <HeroStrip
        icon={ClipboardCheck}
        accent="green"
        eyebrow="Wasabi Red Team"
        title="Status Page"
        subtitle="Company-wide audit trail of every penetration test - completed dates, grades, severity counts, notes, and links to the original report documents."
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIncludeInProgress((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition ${
              includeInProgress
                ? 'border-wasabi-300 bg-wasabi-100 text-wasabi-700'
                : 'border-cyber-300/40 bg-cyber-100/40 text-slate-700 hover:border-cyber-300'
            }`}
            title="Toggle inclusion of pentests not yet completed"
          >
            <Filter className="w-3.5 h-3.5" />
            {includeInProgress ? 'Showing all' : 'Completed only'}
          </button>
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 px-2 py-1.5 rounded hover:bg-cyber-100/40"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <button
          type="button"
          onClick={onCopyMarkdown}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 disabled:opacity-40"
        >
          <Copy className="w-3.5 h-3.5" />
          {copied ? 'Copied to clipboard' : 'Copy as Markdown (paste into Confluence)'}
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
          <Stat label="Total" value={data.total} />
          <Stat label="Completed" value={data.completed_count} accent="green" />
          <Stat label="In progress" value={data.in_progress_count} accent="cyan" />
          <Stat label="Critical" value={aggregateSeverity.critical} accent="red" />
          <Stat label="High" value={aggregateSeverity.high} accent="orange" />
          <Stat label="Medium" value={aggregateSeverity.medium} accent="amber" />
          <Stat label="Low + Info" value={aggregateSeverity.low + aggregateSeverity.info} accent="slate" />
        </div>
      )}

      {error && <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        <table className="w-full text-sm text-left text-slate-800">
          <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
            <tr>
              <th className="px-4 py-3">Pentest</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Completed</th>
              <th className="px-4 py-3 text-center">Grade</th>
              <th className="px-4 py-3 text-center">Crit</th>
              <th className="px-4 py-3 text-center">High</th>
              <th className="px-4 py-3 text-center">Med</th>
              <th className="px-4 py-3 text-center">Low</th>
              <th className="px-4 py-3 text-center">Info</th>
              <th className="px-4 py-3">Report</th>
              <th className="px-4 py-3">Notes</th>
              {isPentester && <th className="px-4 py-3 w-12 text-right"></th>}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={isPentester ? 13 : 12} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isPentester ? 13 : 12} className="px-4 py-8 text-center text-slate-500">No pentests yet.</td></tr>
            ) : rows.map((r) => (
              editingId === r.id ? (
                <RowEditor
                  key={r.id}
                  row={r}
                  onSaved={onRowSaved}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <tr key={r.id} className={`border-t border-cyber-300/30 hover:bg-cyber-200/30 ${
                  r.parent_pentest_id != null ? 'bg-cyber-100/30' : ''
                }`}>
                  <td className="px-4 py-3">
                    {r.parent_pentest_id != null ? (
                      <div className="flex items-start gap-2 pl-6">
                        <span className="text-shinobi-green/60 text-xs font-mono mt-0.5 select-none">↳</span>
                        <div>
                          <Link to={`/pentests/${r.id}/dashboards`} className="text-slate-800 hover:text-shinobi-green text-sm">
                            {r.name}
                          </Link>
                          <div className="text-[9px] text-shinobi-green/60 mt-0.5 uppercase tracking-[0.16em] font-semibold">
                            Retest
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link to={`/pentests/${r.id}/dashboards`} className="text-slate-900 hover:text-shinobi-green font-medium">
                        {r.name}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{TYPE_LABEL[r.type] || r.type}</td>
                  <td className="px-4 py-3"><StatusChip status={r.status} size="sm" /></td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(r.ended_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex w-7 h-7 items-center justify-center rounded font-bold border ${GRADE_PILL[r.grade] || GRADE_PILL.F}`}>
                      {r.grade}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-center font-mono ${r.severity_counts?.critical ? 'text-red-700' : 'text-slate-600'}`}>
                    {r.severity_counts?.critical || 0}
                  </td>
                  <td className={`px-4 py-3 text-center font-mono ${r.severity_counts?.high ? 'text-orange-700' : 'text-slate-600'}`}>
                    {r.severity_counts?.high || 0}
                  </td>
                  <td className={`px-4 py-3 text-center font-mono ${r.severity_counts?.medium ? 'text-yellow-700' : 'text-slate-600'}`}>
                    {r.severity_counts?.medium || 0}
                  </td>
                  <td className={`px-4 py-3 text-center font-mono ${r.severity_counts?.low ? 'text-blue-700' : 'text-slate-600'}`}>
                    {r.severity_counts?.low || 0}
                  </td>
                  <td className={`px-4 py-3 text-center font-mono ${r.severity_counts?.info ? 'text-slate-700' : 'text-slate-600'}`}>
                    {r.severity_counts?.info || 0}
                  </td>
                  <td className="px-4 py-3">
                    {r.external_report_url ? (
                      <a
                        href={r.external_report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-shinobi-green hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </a>
                    ) : (
                      <span className="text-xs text-slate-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700 max-w-md whitespace-pre-wrap">
                    {r.notes || <span className="text-slate-600 italic">No notes</span>}
                  </td>
                  {isPentester && (
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        title="Edit Status Page row"
                        onClick={() => setEditingId(r.id)}
                        className="p-1.5 rounded text-slate-500 hover:text-shinobi-green hover:bg-wasabi-100"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Per-row inline editor for the Status Page. Pentester-only. Edits the four
// fields the operator most commonly touches from this view: name, status,
// date completed (ended_at), external_report_url, notes. Other fields
// (type, classification, scope, score categories) stay editable on the per-PT
// Overview tab. Save commits via PATCH /api/v2/pentests/{id} and the parent
// list refreshes the row in place.
function RowEditor({ row, onSaved, onCancel }) {
  const [name, setName] = useState(row.name || '');
  const [status, setStatus] = useState(row.status || 'planning');
  const [endedAt, setEndedAt] = useState(row.ended_at ? row.ended_at.slice(0, 10) : '');
  const [reportUrl, setReportUrl] = useState(row.external_report_url || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const onSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        name: name.trim() || row.name,
        status,
        // Empty date wipes the ended_at; otherwise convert YYYY-MM-DD to
        // an ISO datetime so the backend timestamp parses cleanly.
        ended_at: endedAt ? new Date(`${endedAt}T00:00:00Z`).toISOString() : null,
        external_report_url: reportUrl.trim() || null,
        notes: notes.trim() || null,
      };
      const updated = await pentestsAPI.update(row.id, payload);
      onSaved(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-wasabi-300 bg-shinobi-green/5">
      <td className="px-4 py-3" colSpan={13}>
        <div className="flex items-start gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-wider text-shinobi-green font-bold mt-1">Editing #{row.id}</div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 disabled:opacity-40"
          >
            <Check className="w-3.5 h-3.5" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-cyber-300/40 text-slate-700 hover:text-slate-900 hover:bg-cyber-100/40 disabled:opacity-40"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pentest name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded px-2 py-1 text-sm text-slate-900 outline-none focus:border-shinobi-green"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status</label>
            <StatusSelect value={status} onChange={setStatus} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Date completed</label>
            <input
              type="date"
              value={endedAt}
              onChange={(e) => setEndedAt(e.target.value)}
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded px-2 py-1 text-sm text-slate-900 outline-none focus:border-shinobi-green"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">External report URL (Microsoft 365 / Confluence / etc)</label>
            <input
              type="url"
              value={reportUrl}
              onChange={(e) => setReportUrl(e.target.value)}
              placeholder="https://wasabi.sharepoint.com/sites/redteam/Shared%20Documents/..."
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded px-2 py-1 text-sm text-slate-900 outline-none focus:border-shinobi-green"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. External attack surface penetration testing for this feature alone. A grade due to no findings."
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded px-2 py-1 text-sm text-slate-900 outline-none focus:border-shinobi-green"
            />
          </div>
        </div>
        {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
      </td>
    </tr>
  );
}

function Stat({ label, value, accent = 'slate' }) {
  const cls = {
    green:  'text-shinobi-green',
    cyan:   'text-cyan-700',
    red:    'text-red-700',
    orange: 'text-orange-700',
    amber:  'text-amber-700',
    slate:  'text-slate-800',
  }[accent];
  return (
    <div className="rounded-lg border border-cyber-300/30 bg-cyber-100/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-mono font-semibold ${cls}`}>{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}

// Render a Confluence-friendly markdown table. Confluence's markdown macro
// understands GitHub Flavored Markdown tables. Notes are inlined verbatim;
// pipe characters and newlines are escaped so they don't break the table.
function renderMarkdown(rows) {
  const esc = (s) => String(s ?? '').replace(/\|/g, '\\|').replace(/\n+/g, ' ');
  const lines = [];
  lines.push('| Pentest | Type | Status | Completed | Grade | Critical | High | Medium | Low | Info | Report | Notes |');
  lines.push('|---|---|---|---|---|---|---|---|---|---|---|---|');
  for (const r of rows) {
    const sc = r.severity_counts || {};
    const reportCell = r.external_report_url ? `[Open](${r.external_report_url})` : '-';
    // Retest rows render with a leading "↳ " so they read as children of the
    // preceding parent line in the pasted Confluence table.
    const namePrefix = r.parent_pentest_id != null ? '↳ ' : '';
    lines.push(
      `| ${namePrefix}${esc(r.name)} | ${esc(TYPE_LABEL[r.type] || r.type)} | ${esc(STATUS_LABEL[r.status] || r.status)} | ${esc(fmtDate(r.ended_at))} | ${esc(r.grade)} | ${sc.critical || 0} | ${sc.high || 0} | ${sc.medium || 0} | ${sc.low || 0} | ${sc.info || 0} | ${reportCell} | ${esc(r.notes || '')} |`
    );
  }
  return lines.join('\n');
}
